<script lang="ts">
/**
 * Three speeds of work, drawn on a line: without Pre6 you walk, Pre6 Apps are manufacturing's first
 * flight, Pre6 Enterprise is space travel. The stretch from walking to first flight is dotted footsteps;
 * the stretch to space is a solid Pre6 red bar, with a white streak that keeps launching along it. No
 * numbers, just the order of magnitude.
 */
import Footprints from "lucide-svelte/icons/footprints";
import Rocket from "lucide-svelte/icons/rocket";
import { onMount } from "svelte";

let root: HTMLDivElement;

onMount(() => {
	const io = new IntersectionObserver(
		([e]) => {
			if (!e.isIntersecting) return;
			root.setAttribute("data-seen", "");
			io.disconnect();
		},
		{ threshold: 0.4 },
	);
	io.observe(root);
	return () => io.disconnect();
});
</script>

<div class="speed" bind:this={root}>
	<ol aria-label="Three speeds of work">
		<li>
			<span class="ic"><Footprints size={16} strokeWidth={2} /></span>
			<span class="t"><b>Without Pre6</b><small>Walking</small></span>
		</li>
		<li class="seg slow" aria-hidden="true"></li>
		<li class="fly">
			<span class="ic">
				<!-- The Wright Flyer: two wings, struts, the elevator out front and the rudder behind. -->
				<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"
					><path d="M6 9h14M6 14h14M8 9v5M13 9v5M18 9v5M2 11.5h4M2 10v3M20 11.5h2.5M22.5 9.5v4" /></svg
				>
			</span>
			<span class="t"><b>Pre6 Apps</b><small>First flight</small></span>
		</li>
		<li class="seg fast" aria-hidden="true"><i></i></li>
		<li class="space">
			<span class="ic"><Rocket size={16} strokeWidth={2} /></span>
			<span class="t"><b>Pre6 Enterprise</b><small>Space travel</small></span>
		</li>
	</ol>
</div>

<style>
	.speed {
		--ease: cubic-bezier(0.2, 0.75, 0.2, 1);
		padding: 26px var(--gutter, 32px) 0;
	}

	ol {
		display: grid;
		grid-template-columns: auto minmax(32px, 1fr) auto minmax(56px, 2.2fr) auto;
		align-items: center;
		gap: 14px;
	}

	li:not(.seg) {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.ic {
		display: grid;
		flex: none;
		place-items: center;
		width: 34px;
		height: 34px;
		border-radius: 50%;
		background: #fff;
		box-shadow: inset 0 0 0 1.5px var(--border);
		color: var(--muted-foreground);
	}

	.fly .ic {
		box-shadow: inset 0 0 0 1.5px var(--ink);
		color: var(--ink);
	}

	.space .ic {
		width: 40px;
		height: 40px;
		background: var(--primary);
		box-shadow: 0 10px 22px -10px var(--primary);
		color: #fff;
	}

	.t {
		display: flex;
		flex-direction: column;
		gap: 3px;
	}

	.t b {
		color: var(--muted-foreground);
		font-size: 13.5px;
		font-weight: 600;
		line-height: 1.2;
	}

	.fly .t b {
		color: var(--ink);
	}

	.space .t b {
		color: var(--ink);
		font-size: 15px;
		font-weight: 700;
	}

	.t small {
		color: var(--muted-foreground);
		font: 500 11px/1.2 var(--font-mono);
		letter-spacing: 0.04em;
		text-transform: uppercase;
	}

	.space .t small {
		color: var(--primary);
	}

	/* Walking to first flight: footsteps. First flight to space: one solid line, ink into red. */
	.seg {
		position: relative;
		height: 2px;
		overflow: hidden;
		transform-origin: left;
	}

	.slow {
		background: repeating-linear-gradient(90deg, color-mix(in oklch, var(--muted-foreground) 45%, transparent) 0 3px, transparent 3px 9px);
	}

	/* Same bar as the "How fast, how sure" row: solid Pre6 red, with the white streak running along it. */
	.fast {
		height: 4px;
		border-radius: 999px;
		background: var(--primary);
	}

	/* A streak that keeps launching along the fast stretch, with a short breath between runs. */
	.fast i {
		position: absolute;
		inset: 0 auto 0 0;
		width: 30%;
		background: linear-gradient(90deg, transparent, #fff 70%, transparent);
		opacity: 0;
	}

	/* Entrances: each stop lands in turn, the slow stretch steps in, the fast one shoots across. */
	li:not(.seg) {
		opacity: 0;
		translate: 0 8px;
	}

	.seg {
		transform: scaleX(0);
	}

	.speed:global([data-seen]) li:not(.seg) {
		opacity: 1;
		translate: none;
		transition:
			opacity 0.4s var(--ease),
			translate 0.5s var(--ease);
	}

	.speed:global([data-seen]) .fly {
		transition-delay: 0.7s;
	}

	.speed:global([data-seen]) .space {
		transition-delay: 1.15s;
	}

	.speed:global([data-seen]) .slow {
		transform: none;
		transition: transform 0.6s steps(6, end) 0.15s;
	}

	.speed:global([data-seen]) .fast {
		transform: none;
		transition: transform 0.35s cubic-bezier(0.7, 0, 0.84, 0) 0.85s;
	}

	.speed:global([data-seen]) .fast i {
		animation: streak 2.4s cubic-bezier(0.65, 0, 0.35, 1) 1.3s infinite;
	}

	@keyframes streak {
		0% {
			opacity: 0.9;
			translate: -100% 0;
		}
		55% {
			opacity: 0.9;
			translate: 340% 0;
		}
		56%,
		100% {
			opacity: 0;
			translate: 340% 0;
		}
	}

	@media (max-width: 767px) {
		.speed {
			padding-inline: 20px;
		}

		ol {
			gap: 8px;
		}

		li:not(.seg) {
			flex-direction: column;
			align-items: center;
			gap: 6px;
			max-width: 76px;
			text-align: center;
		}

		.t b {
			font-size: 11.5px;
		}

		.space .t b {
			font-size: 12px;
		}

		.t small {
			font-size: 9.5px;
		}

		.seg {
			align-self: start;
			margin-top: 17px;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		li:not(.seg),
		.seg {
			opacity: 1;
			translate: none;
			transform: none;
			transition: none !important;
		}

		.fast i {
			display: none;
		}
	}
</style>
